import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import Intercom from '@intercom/messenger-js-sdk';
import { AuthService } from './auth.service';
import { ContextService } from './context.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class IntercomService implements OnDestroy {

  private initialized = false;
  private sdkLoaded = false;
  private routeSub: Subscription | null = null;
  private moduleSub: Subscription | null = null;
  private clinicSub: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private contextService: ContextService,
    private router: Router
  ) {}

  /**
   * Verifica se o Intercom está habilitado no ambiente atual.
   * Só será ativo quando houver um intercomAppId configurado no environment.
   */
  private isEnabled(): boolean {
    return !!(environment as any).intercomAppId;
  }

  /**
   * Inicializa o Intercom com os dados do usuário logado.
   * Deve ser chamado após o login bem-sucedido.
   */
  boot(): void {
    console.log('[Intercom] boot() chamado');
    console.log('[Intercom] isEnabled:', this.isEnabled());

    if (!this.isEnabled()) {
      console.warn('[Intercom] Intercom desabilitado — intercomAppId vazio no environment');
      return;
    }

    const appId = (environment as any).intercomAppId;

    // Obtém o JWT gerado pelo backend (Identity Verification via JWT)
    // O JWT contém claims sensíveis: user_id, email, name, phone, companies
    const userJwt = this.authService.getIntercomJwt();
    console.log('[Intercom] JWT do backend:', userJwt ? '✓ (' + userJwt.substring(0, 20) + '...)' : '✗ vazio');

    // Detecta informações do navegador e SO
    const browserInfo = this.getBrowserInfo();

    // Config para Identity Verification via JWT
    // Atributos sensíveis (user_id, email, name, phone, companies) estão DENTRO do JWT
    // Atributos não-sensíveis/dinâmicos são passados diretamente (low-fidelity data)
    const currentModule = this.authService.getSelectedModule();
    const intercomConfig: Record<string, any> = {
      app_id: appId,
      intercom_user_jwt: userJwt || undefined,

      // Atributos não-sensíveis / dinâmicos — fora do JWT
      browser: browserInfo.name,
      browser_version: browserInfo.version,
      os: browserInfo.os,
      "Module Name": currentModule?.description || 'Nenhum',
      "Screen Name": this.getScreenName(),
      "Screen URL": window.location.href,

      // Duração da sessão: 1 dia (recomendado pela doc do Intercom para JWT)
      session_duration: 86400000,
    };

    console.log('[Intercom] Config sendo enviado:', JSON.stringify(intercomConfig, null, 2));

    try {
      // Shutdown da sessão anterior se existir
      if (this.initialized && window.Intercom) {
        console.log('[Intercom] Fazendo shutdown da sessão anterior...');
        window.Intercom('shutdown');
        this.initialized = false;
      }

      if (!this.sdkLoaded) {
        // Primeiro boot: SDK carrega o script do Intercom
        // Passamos o config completo para que o auto-boot use o JWT
        Intercom(intercomConfig as any);
        this.sdkLoaded = true;
      } else {
        // Re-boot (pós shutdown/login): usa window.Intercom('boot', ...) diretamente
        // O SDK Intercom() só carrega o script na primeira chamada — chamadas
        // subsequentes executam 'update' ao invés de 'boot', o que não cria nova sessão.
        if (window.Intercom) {
          window.Intercom('boot', intercomConfig);
        }
      }

      this.initialized = true;
      console.log('[Intercom] ✓ Intercom inicializado com sucesso');

      // Escuta mudanças de rota, módulo e clínica
      this.listenToRouteChanges();
      this.listenToModuleChanges();
      this.listenToClinicChanges();

      // Força um update inicial após um curto delay para garantir que os dados
      // sejam enviados ao Intercom após a primeira navegação pós-login
      setTimeout(() => {
        const mod = this.authService.getSelectedModule();
        const screenName = this.getScreenName();
        const screenUrl = window.location.href;
        console.log('[Intercom] Update inicial forçado:', { screenName, screenUrl, module: mod?.description });
        this.update({
          "Module Name": mod?.description || 'Nenhum',
          "Screen Name": screenName,
          "Screen URL": screenUrl,
        });
      }, 1500);

    } catch (error) {
      console.error('[Intercom] ✗ Erro ao inicializar o Intercom:', error);
    }
  }

  /**
   * Atualiza os dados do Intercom (ex: ao mudar de tela).
   * A sessão já está autenticada via JWT no boot — updates
   * ocorrem dentro da sessão ativa (session_duration: 1 dia).
   */
  private update(data: Record<string, any> = {}): void {
    if (!this.initialized || !this.isEnabled()) {
      return;
    }

    try {
      if (window.Intercom) window.Intercom('update', data);
    } catch (error) {
      console.error('[Intercom] Erro ao atualizar:', error);
    }
  }

  /**
   * Encerra a sessão do Intercom. Deve ser chamado no logout.
   */
  shutdown(): void {
    if (!this.initialized || !this.isEnabled()) {
      return;
    }

    try {
      // Cancela subscrições
      if (this.routeSub) {
        this.routeSub.unsubscribe();
        this.routeSub = null;
      }
      if (this.moduleSub) {
        this.moduleSub.unsubscribe();
        this.moduleSub = null;
      }
      if (this.clinicSub) {
        this.clinicSub.unsubscribe();
        this.clinicSub = null;
      }
      if (window.Intercom) window.Intercom('shutdown');
      this.initialized = false;
      console.log('[Intercom] Sessão encerrada');
    } catch (error) {
      console.error('[Intercom] Erro ao encerrar:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
      this.routeSub = null;
    }
    if (this.moduleSub) {
      this.moduleSub.unsubscribe();
      this.moduleSub = null;
    }
    if (this.clinicSub) {
      this.clinicSub.unsubscribe();
      this.clinicSub = null;
    }
  }

  /**
   * Escuta mudanças de rota e atualiza o Intercom com a nova tela.
   * Cancela subscrição anterior para evitar duplicações.
   */
  private listenToRouteChanges(): void {
    // Evita múltiplas subscrições se boot() for chamado mais de uma vez
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }

    this.routeSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const navEvent = event as NavigationEnd;
        const mod = this.authService.getSelectedModule();
        const screenName = this.getScreenNameFromUrl(navEvent.urlAfterRedirects || navEvent.url);
        const screenUrl = window.location.origin + (navEvent.urlAfterRedirects || navEvent.url);
        console.log('[Intercom] Rota mudou:', { screenName, screenUrl, module: mod?.description });
        // Atualiza atributos do contato no Intercom
        this.update({
          "Module Name": mod?.description || 'Nenhum',
          "Screen Name": screenName,
          "Screen URL": screenUrl,
        });
      });
  }

  /**
   * Escuta mudanças de módulo selecionado (pode ocorrer sem navegação de rota).
   */
  private listenToModuleChanges(): void {
    if (this.moduleSub) {
      this.moduleSub.unsubscribe();
    }

    this.moduleSub = this.authService.selectedModule$.subscribe((mod) => {
      if (!this.initialized || !this.isEnabled()) return;
      const moduleName = mod?.description || 'Nenhum';
      console.log('[Intercom] Módulo mudou:', moduleName);
      this.update({ "Module Name": moduleName });
    });
  }

  /**
   * Escuta mudanças de clínica ativa (multi-clínica).
   * Atualiza o Intercom com o nome da clínica ativa quando o usuário troca de clínica.
   */
  private listenToClinicChanges(): void {
    if (this.clinicSub) {
      this.clinicSub.unsubscribe();
    }

    this.clinicSub = this.contextService.getActiveClinic().subscribe((clinic) => {
      if (!this.initialized || !this.isEnabled()) return;
      if (!clinic?.cnpj) return;
      const clinicName = clinic.name || 'Desconhecida';
      console.log('[Intercom] Clínica mudou:', clinicName);
      this.update({
        "Company Name": clinicName,
        company: {
          company_id: clinic.cnpj,
          name: clinicName,
        }
      });
    });
  }

  /**
   * Retorna o nome amigável da tela atual baseado na URL.
   */
  private getScreenName(): string {
    return this.getScreenNameFromUrl(window.location.pathname);
  }

  /**
   * Retorna o nome amigável baseado em um path/URL fornecido.
   * Cobre rotas V2, rotas dinâmicas de módulo e sub-páginas.
   */
  private getScreenNameFromUrl(url: string): string {
    // Remove query params e hash para pegar apenas o pathname
    const path = url.split('?')[0].split('#')[0];

    // --- Rotas de nível superior (fora do /main) ---
    const topLevelScreens: Record<string, string> = {
      '/login': 'Login',
      '/forgot-password': 'Esqueci a Senha',
      '/verify-code': 'Verificar Código',
      '/upload-photos': 'Upload de Fotos',
      '/upload-treatment-photos': 'Upload de Fotos de Tratamento',
      '/anamnese-invalid': 'Anamnese Inválida',
      '/anamnese-success': 'Anamnese Concluída',
      '/link-invalid': 'Link Inválido',
    };
    if (topLevelScreens[path]) return topLevelScreens[path];

    // --- Layout V2: /main/v2/... ---
    if (path.startsWith('/main/v2/')) {
      const v2Path = path.replace('/main/v2/', '');
      const segments = v2Path.split('/');

      // Rotas dinâmicas de módulo: modules/:moduleName/...
      if (segments[0] === 'modules' && segments.length >= 2) {
        const moduleName = decodeURIComponent(segments[1]);
        return this.resolveEntityScreen(segments.slice(2), moduleName);
      }

      // Mapeamento de seções V2 para nomes amigáveis
      const v2Sections: Record<string, string> = {
        'company': 'Empresas',
        'providers': 'Prestadores',
        'users': 'Usuários',
        'procedures': 'Procedimentos',
        'patients': 'Pacientes',
        'alunos': 'Alunos',
        'terceiros': 'Terceiros',
        'anamneses': 'Anamneses Pendentes',
        'signatures': 'Assinaturas Pendentes',
        'treatment-plans': 'Planos de Tratamento',
        'templates': 'Templates',
        'dashboard': 'Dashboard',
        'clinic-groups': 'Grupos de Clínicas',
      };

      const section = segments[0];
      const sectionName = v2Sections[section];

      if (sectionName) {
        // Seções com sub-rotas de entidade (patients, alunos, terceiros, company, etc.)
        if (['patients', 'alunos', 'terceiros'].includes(section)) {
          return this.resolveEntityScreen(segments.slice(1), sectionName);
        }

        // treatment-plan/:id (detalhe)
        if (section === 'treatment-plan' && segments.length >= 2) {
          return 'Detalhes do Plano';
        }

        // Sub-rotas genéricas (create, :id/edit)
        if (segments.length >= 2) {
          if (segments[1] === 'create') return `Criar ${this.singularize(sectionName)}`;
          if (segments.length >= 3 && segments[2] === 'edit') return `Editar ${this.singularize(sectionName)}`;
        }

        return sectionName;
      }

      // treatment-plan/:id (rota sem 's')
      if (segments[0] === 'treatment-plan') {
        return 'Detalhes do Plano';
      }
    }

    return path;
  }

  /**
   * Resolve o nome da tela para sub-rotas de entidade (pacientes, alunos, terceiros, módulos dinâmicos).
   */
  private resolveEntityScreen(subSegments: string[], contextName: string): string {
    if (subSegments.length === 0) return contextName;

    const sub = subSegments[0];

    if (sub === 'create') return `${contextName} > Criar`;

    // :id/edit
    if (subSegments.length >= 2 && subSegments[1] === 'edit') return `${contextName} > Ficha`;

    // anamnese/create
    if (sub === 'anamnese' && subSegments.length >= 2) {
      if (subSegments[1] === 'create') return `${contextName} > Novo Formulário`;
      // anamnese/:id/view
      if (subSegments.length >= 3 && subSegments[2] === 'view') return `${contextName} > Formulário Enviado`;
      // anamnese/:id/answers
      if (subSegments.length >= 3 && subSegments[2] === 'answers') return `${contextName} > Respostas do Formulário`;
      // anamnese/:id
      return `${contextName} > Ficha de Formulário`;
    }

    // generate-docs/:id
    if (sub === 'generate-docs') return `${contextName} > Gerar Documentos`;

    // document-form | document-form/success
    if (sub === 'document-form') {
      if (subSegments.length >= 2 && subSegments[1] === 'success') return `${contextName} > Documentos Gerados`;
      return `${contextName} > Preencher Documentos`;
    }

    // standalone-docs
    if (sub === 'standalone-docs') return `${contextName} > Documentos Avulsos`;

    // forms (formulários pendentes do módulo)
    if (sub === 'forms') return `${contextName} > Formulários`;

    // signatures (assinaturas do módulo)
    if (sub === 'signatures') return `${contextName} > Assinaturas`;

    return contextName;
  }

  /**
   * Tenta singularizar nomes de seção para uso em "Criar X", "Editar X".
   */
  private singularize(name: string): string {
    const map: Record<string, string> = {
      'Empresas': 'Empresa',
      'Prestadores': 'Prestador',
      'Usuários': 'Usuário',
      'Procedimentos': 'Procedimento',
      'Pacientes': 'Paciente',
      'Alunos': 'Aluno',
      'Terceiros': 'Terceiro',
      'Templates': 'Template',
      'Planos de Tratamento': 'Plano de Tratamento',
      'Grupos de Clínicas': 'Grupo de Clínicas',
    };
    return map[name] || name;
  }

  /**
   * Detecta informações do navegador e sistema operacional.
   */
  private getBrowserInfo(): { name: string; version: string; os: string } {
    const ua = navigator.userAgent;
    let browserName = 'Desconhecido';
    let browserVersion = '';
    let os = 'Desconhecido';

    // Detectar navegador
    if (ua.includes('Firefox/')) {
      browserName = 'Firefox';
      browserVersion = ua.split('Firefox/')[1]?.split(' ')[0] || '';
    } else if (ua.includes('Edg/')) {
      browserName = 'Edge';
      browserVersion = ua.split('Edg/')[1]?.split(' ')[0] || '';
    } else if (ua.includes('Chrome/')) {
      browserName = 'Chrome';
      browserVersion = ua.split('Chrome/')[1]?.split(' ')[0] || '';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      browserName = 'Safari';
      browserVersion = ua.split('Version/')[1]?.split(' ')[0] || '';
    }

    // Detectar SO
    if (ua.includes('Windows NT 10')) {
      os = ua.includes('Windows NT 10.0; Win64') ? 'Windows 10/11' : 'Windows 10';
    } else if (ua.includes('Windows NT')) {
      os = 'Windows';
    } else if (ua.includes('Mac OS X')) {
      const macVersion = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
      os = macVersion ? `macOS ${macVersion[1].replace(/_/g, '.')}` : 'macOS';
    } else if (ua.includes('Linux')) {
      os = 'Linux';
    } else if (ua.includes('Android')) {
      os = 'Android';
    } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
      os = 'iOS';
    }

    return { name: browserName, version: browserVersion, os };
  }
}
